/**
 * Reusable camera barcode-scanner modal (native BarcodeDetector).
 *
 * Opens the rear camera, polls the live video at ~5Hz, and fires `onDetected`
 * with the first decoded value (then the caller closes the modal). Tears the
 * camera down on close / unmount — stops every MediaStreamTrack and clears the
 * <video> srcObject so the camera light goes off.
 *
 * Support is narrow on purpose: `BarcodeDetector` + `navigator.mediaDevices`
 * are Chromium-only (Chrome on Android ≥83) and require a SECURE CONTEXT
 * (HTTPS or localhost). Callers should gate the entry-point button on the
 * exported `cameraScanSupported` flag; the modal still self-guards secure
 * context + permission failures and reports them via `onError`. iOS Safari and
 * desktop Firefox lack BarcodeDetector — those users fall back to a USB-wedge
 * scanner or password.
 */

import { useEffect, useRef, useState } from "react";
import { Modal, Typography } from "antd";

import {
  type BarcodeDetectorLike,
  cameraScanSupported,
} from "../utils/cameraScan";

const { Text } = Typography;

export function CameraScanModal({
  open,
  onClose,
  onDetected,
  onError,
  title = "Scan barcode",
  hint = "Point the rear camera at the barcode.",
}: {
  open: boolean;
  onClose: () => void;
  onDetected: (code: string) => void;
  onError?: (message: string) => void;
  title?: string;
  hint?: string;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [flash, setFlash] = useState(false);

  // Latest-ref pattern: keep the camera effect keyed only on `open` so a parent
  // re-render (which re-creates these callbacks) never restarts the stream.
  const cbRef = useRef({ onClose, onDetected, onError });
  useEffect(() => {
    cbRef.current = { onClose, onDetected, onError };
  });

  useEffect(() => {
    if (!open) return;
    const cb = cbRef.current;
    if (typeof window !== "undefined" && !window.isSecureContext) {
      cb.onError?.(
        "Camera scanning requires HTTPS (or localhost). Use a USB-wedge scanner or password over plain HTTP.",
      );
      cb.onClose();
      return;
    }
    if (!cameraScanSupported) {
      cb.onError?.(
        "This browser can't scan with the camera (needs Chrome on Android ≥83 over HTTPS). Use a USB-wedge scanner or password.",
      );
      cb.onClose();
      return;
    }

    let cancelled = false;
    let done = false; // fire at most one detection per open
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let detector: BarcodeDetectorLike | null = null;
    // Track the video element actually bound to the stream so cleanup releases
    // it even if videoRef.current later points at a freshly-mounted node.
    let boundVideo: HTMLVideoElement | null = null;

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          boundVideo = video;
          video.srcObject = stream;
          await video.play().catch(() => undefined);
        }
        const Ctor = window.BarcodeDetector;
        if (!Ctor) return;
        detector = new Ctor();
        intervalId = setInterval(async () => {
          if (cancelled || done) return;
          const v = videoRef.current;
          if (!v || v.readyState < 2 || !detector) return;
          try {
            const codes = await detector.detect(v);
            if (cancelled || done) return;
            const code = codes[0]?.rawValue;
            if (code) {
              done = true;
              if (intervalId) clearInterval(intervalId);
              setFlash(true);
              cbRef.current.onDetected(code);
            }
          } catch {
            // detect() throws on intermittent frames — best-effort, ignore.
          }
        }, 200);
      } catch {
        if (cancelled) return;
        cb.onError?.("Camera unavailable — check browser permissions.");
        cb.onClose();
      }
    };
    void start();

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      const stream = streamRef.current;
      if (stream) stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      if (boundVideo) boundVideo.srcObject = null;
      setFlash(false);
    };
  }, [open]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={title}
      width="90%"
      destroyOnHidden
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          background: "black",
          borderRadius: 8,
          overflow: "hidden",
          outline: flash ? "4px solid #52c41a" : "none",
        }}
      >
        <video
          ref={videoRef}
          playsInline
          muted
          style={{ width: "100%", height: "auto", display: "block" }}
        />
      </div>
      <Text
        type="secondary"
        style={{ fontSize: 12, display: "block", marginTop: 8 }}
      >
        {hint}
      </Text>
    </Modal>
  );
}
